// =============================================================================
// config.ts — Types, environment detection, and config resolution for MediaSync
// =============================================================================

export type Environment = 'dev' | 'test' | 'prod';

export interface MediaSyncHandlers {
  fetchListings: (
    pushListing: (listing: Record<string, unknown>, isReady?: boolean) => void,
  ) => Promise<void>;
  fetchListingDetails: (
    partialListing: Record<string, unknown>,
    pushListing: (listing: Record<string, unknown>) => void,
  ) => Promise<void>;
  uploadMedia: (
    mediaEntry: Record<string, unknown>,
    listing: Record<string, unknown>,
    context: Record<string, unknown>,
  ) => Promise<void>;
  importStart?: (
    mediaEntries: Record<string, unknown>[],
    listing: Record<string, unknown>,
    context: Record<string, unknown>,
  ) => Promise<void>;
  importComplete: (context: Record<string, unknown>) => Promise<void>;
}

export interface MediaSyncProps {
  apiKey: string;
  orgId: string;
  pluginUrl: string;
  agentName: string;
  handlers: MediaSyncHandlers;
}

export function detectEnvironment(): Environment {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'dev';
  if (
    hostname.includes('.dev.') ||
    hostname.includes('-dev') ||
    hostname.includes('local.')
  )
    return 'dev';
  if (
    hostname.includes('.tst.') ||
    hostname.includes('-tst') ||
    hostname.includes('.test.')
  )
    return 'test';
  return 'prod';
}

const PLUGIN_URLS: Record<Environment, string> = {
  dev: 'https://media.dev.bright-solutions.co',
  test: 'https://media.tst.bright-solutions.co',
  prod: 'https://media.bright-solutions.co',
};

const ORG_IDS: Record<Environment, string> = {
  dev: 'BMLS-DEV',
  test: 'BMLS-TEST',
  prod: 'BMLS-PRODUCTION',
};

export function resolveMediaSyncConfig(
  tenantConfig: Record<string, string> | null,
  env: Environment,
): { apiKey: string; orgId: string; pluginUrl: string } {
  // Tenant config is primary; localStorage is a dev fallback
  const apiKey =
    tenantConfig?.MEDIA_SYNC_API_KEY ||
    localStorage.getItem('MediaSyncApiKey') ||
    '';

  let orgId = tenantConfig?.MEDIA_SYNC_API_USER || ORG_IDS[env] || ORG_IDS.test;
  let pluginUrl =
    tenantConfig?.MEDIA_SYNC_PLUGIN_URL || PLUGIN_URLS[env] || PLUGIN_URLS.test;

  // localStorage overrides for dev testing
  const lsApiUser = localStorage.getItem('MediaSyncApiUser');
  const lsPluginUrl = localStorage.getItem('MediaSyncPluginUrl');
  if (lsApiUser) orgId = lsApiUser;
  if (lsPluginUrl) pluginUrl = lsPluginUrl;

  return { apiKey, orgId, pluginUrl };
}
